/**
 * FORM INPUT COMPONENT
 *
 * Reusable text input with consistent styling.
 * Supports labels, help text, error states, and icons.
 *
 * Features:
 * - Dark/light variants
 * - Label and help text
 * - Error state with message
 * - Leading/trailing icons
 * - All standard input types
 *
 * @example
 * <FormInput
 *   label="Polish Name"
 *   value={name}
 *   onChange={(e) => setName(e.target.value)}
 *   placeholder="Enter name..."
 *   required
 * />
 *
 * <FormInput
 *   label="Price"
 *   type="number"
 *   leadingIcon={<span>$</span>}
 *   variant="dark"
 * />
 */

import { type InputHTMLAttributes, type ReactNode, forwardRef } from 'react';

export type InputVariant = 'light' | 'dark';

export interface FormInputProps extends Omit<InputHTMLAttributes<HTMLInputElement>, 'size'> {
  /** Label text above input */
  label?: string;
  /** Help text below input */
  helpText?: string;
  /** Error message (also sets error styling) */
  error?: string;
  /** Color variant */
  variant?: InputVariant;
  /** Icon/element at start of input */
  leadingIcon?: ReactNode;
  /** Icon/element at end of input */
  trailingIcon?: ReactNode;
  /** Additional class for wrapper */
  wrapperClassName?: string;
  /** Additional class for input */
  className?: string;
}

const variantClasses: Record<InputVariant, { wrapper: string; input: string; label: string; help: string }> = {
  light: {
    wrapper: '',
    input: 'border-gray-300 bg-white text-gray-900 placeholder-gray-400 focus:ring-purple-500 focus:border-purple-500',
    label: 'text-gray-700',
    help: 'text-gray-500',
  },
  dark: {
    wrapper: '',
    input: 'border-slate-600 bg-slate-700/50 text-glass placeholder-slate-400 focus:ring-purple-500 focus:border-transparent',
    label: 'text-glass-secondary',
    help: 'text-glass-muted',
  },
};

export const FormInput = forwardRef<HTMLInputElement, FormInputProps>(
  (
    {
      label,
      helpText,
      error,
      variant = 'light',
      leadingIcon,
      trailingIcon,
      wrapperClassName = '',
      className = '',
      id,
      ...props
    },
    ref
  ) => {
    const inputId = id || label?.toLowerCase().replace(/\s+/g, '-');
    const styles = variantClasses[variant];
    const hasError = !!error;

    const inputClasses = [
      'w-full rounded-lg border transition-colors',
      'focus:outline-none focus:ring-2',
      styles.input,
      hasError ? 'border-red-500 focus:ring-red-500' : '',
      leadingIcon ? 'pl-10' : 'px-3',
      trailingIcon ? 'pr-10' : 'px-3',
      'py-2',
      className,
    ]
      .filter(Boolean)
      .join(' ');

    return (
      <div className={wrapperClassName}>
        {label && (
          <label
            htmlFor={inputId}
            className={`block text-sm font-medium mb-1 ${styles.label}`}
          >
            {label}
            {props.required && <span className="text-red-500 ml-1">*</span>}
          </label>
        )}

        <div className="relative">
          {leadingIcon && (
            <span className={`absolute left-3 top-1/2 -translate-y-1/2 ${variant === 'dark' ? 'text-glass-muted' : 'text-gray-400'}`}>
              {leadingIcon}
            </span>
          )}

          <input
            ref={ref}
            id={inputId}
            className={inputClasses}
            aria-invalid={hasError}
            aria-describedby={error ? `${inputId}-error` : helpText ? `${inputId}-help` : undefined}
            {...props}
          />

          {trailingIcon && (
            <span className={`absolute right-3 top-1/2 -translate-y-1/2 ${variant === 'dark' ? 'text-glass-muted' : 'text-gray-400'}`}>
              {trailingIcon}
            </span>
          )}
        </div>

        {error && (
          <p id={`${inputId}-error`} className="mt-1 text-sm text-red-500">
            {error}
          </p>
        )}

        {helpText && !error && (
          <p id={`${inputId}-help`} className={`mt-1 text-sm ${styles.help}`}>
            {helpText}
          </p>
        )}
      </div>
    );
  }
);

FormInput.displayName = 'FormInput';

export default FormInput;
